import { useTranslation } from 'react-i18next'
import { ErrorBoundary, Typogaphy, Button } from 'vnetwork-ui'
import { FileText, Wrench, Mail } from 'lucide-react'
import { Link } from 'react-router-dom'
import ResolvedNormalTicket from './ResolveNormalTicket'
import ResolvedIncidentTicket from './Resolve'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getService } from '@/modules/Service/services'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import InternalTicketWorkflowByTicket from '@/modules/Ticket/components/Forms/PreviewServiceInformation/InternalTicketWorkflowByTicket'
import { generatePathDatacentral, paths } from '@/utils/path'

interface TicketMajorProps {
    ticket: any
}

export const TICKET_TYPE_RCA_IDS = [2, 5]

const Inprogress: React.FC<TicketMajorProps> = ({ ticket }) => {
    const { t } = useTranslation()

    // Check if ticket has service workflow
    const hasServiceWorkflow =
        ticket?.ticket_type_group === 'service' &&
        ticket?.service_id &&
        ticket?.service_id > 0

    // Fetch service data if needed for workflow
    const { data: service } = useQueryWithHandler({
        queryFn: () => getService(ticket?.service_id as number),
        queryKey: [QUERY_KEYS.SERVICE_DETAIL, ticket?.service_id, 'workflow'],
        enabled: hasServiceWorkflow,
    })

    // Determine which resolve form to show
    const showRCAForm = TICKET_TYPE_RCA_IDS?.includes(ticket?.ticket_type_id)
    const showResolveForm = ticket?.ticket_type_group !== 'service'

    // Check if can send service notification
    const canSendServiceNotification =
        hasServiceWorkflow &&
        (ticket?.ticket_type_key === 'service_creating' ||
            ticket?.ticket_type_key === 'service_creating_poc') &&
        ticket?.service_id

    return (
        <div className="space-y-4">
            {/* Processing info */}
            <div className="flex items-center gap-2 text-gray-600 bg-amber-50 px-4 py-2 rounded-lg">
                <Wrench className="w-4 h-4 text-amber-600" />
                <Typogaphy size="small" className="text-amber-700">
                    {t('ticket-inprogress-hint')}
                </Typogaphy>
            </div>

            {/* Internal Ticket Workflow - for service tickets */}
            {hasServiceWorkflow && service && (
                <ErrorBoundary>
                    <InternalTicketWorkflowByTicket
                        ticketId={ticket.ticket_id}
                        service={service}
                    />
                </ErrorBoundary>
            )}

            {/* Send Service Notification - for service creating tickets */}
            {canSendServiceNotification && (
                <div className="flex justify-end">
                    <Link
                        to={`${generatePathDatacentral(paths.serviceDetail, { id: ticket.service_id })}?tab=service-notification&action=create-notification&ticket_id=${ticket.ticket_id}`}
                        target="_blank"
                    >
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Mail className="w-4 h-4" />}
                        >
                            {t('send-service-information')}
                        </Button>
                    </Link>
                </div>
            )}

            {/* Resolve forms - for non-service tickets */}
            {showResolveForm && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* Form header */}
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-primary" />
                        <Typogaphy className="font-medium text-sm">
                            {t('ticket-resolve-form-title')}
                        </Typogaphy>
                    </div>

                    {/* Form content */}
                    <div className="p-4">
                        {showRCAForm ? (
                            <ResolvedIncidentTicket ticket={ticket} />
                        ) : (
                            <ResolvedNormalTicket ticket={ticket} />
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default Inprogress
